/**
 * wavEncoderSetup.js
 * 
 * Initializes the WAV encoder for ExtendableMediaRecorder
 * This ensures each audio chunk is a complete, independently playable WAV file
 * 
 * Must be called once during app startup before any recording is initiated
 */

import { MediaRecorder as ExtendableMediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';
import logger from './Logger';

let wavEncoderInitialized = false;

/**
 * Initialize WAV encoder for MediaRecorder
 * Call this once during app startup (e.g., in index.js or App.js)
 * 
 * CRITICAL: This must run BEFORE creating any MediaRecorder instances
 * and must run only ONCE to avoid double-registration
 */
export async function initializeWavEncoder() {
  if (wavEncoderInitialized) {
    logger.debug('WAV encoder already initialized', {
      component: 'wavEncoderSetup',
      action: 'ENCODER_ALREADY_INITIALIZED'
    });
    return;
  }

  try {
    // Register the WAV encoder with ExtendableMediaRecorder
    // This must be done once globally before any recording starts
    const wavEncoder = await connect();
    await register(wavEncoder);
    wavEncoderInitialized = true;

    logger.info('WAV encoder initialized successfully', {
      component: 'wavEncoderSetup',
      action: 'ENCODER_INITIALIZED',
      context: { timestamp: Date.now(), encoderType: 'WAV' }
    });

    return true;
  } catch (error) {
    logger.error('Failed to initialize WAV encoder', {
      component: 'wavEncoderSetup',
      action: 'ENCODER_INITIALIZATION_FAILED',
      error,
      context: { timestamp: Date.now() }
    });

    throw error;
  }
}

/**
 * Get ExtendableMediaRecorder class
 * Use this instead of native MediaRecorder for WAV encoding support
 */
export function getExtendableMediaRecorder() {
  if (!wavEncoderInitialized) {
    logger.warn('WAV encoder not initialized - ensure initializeWavEncoder() was called', {
      component: 'wavEncoderSetup',
      action: 'ENCODER_NOT_INITIALIZED'
    });
  }
  return ExtendableMediaRecorder;
}

/**
 * Check if WAV encoder is ready
 */
export function isWavEncoderReady() {
  return wavEncoderInitialized;
}
